import api from './api';
import type { InventoryBalance, InventoryMovement, MovementType, PaginatedResponse } from '../types/venue';

const base = (venueId: string) => `/admin/venue/${venueId}/inventory/movement`;

export interface ManualMovementPayload {
  inventoryItemId: string;
  /** The VenueLocation where this movement is posted. Required. */
  locationId: string;
  /** Phase 6: only MANUAL_ADJUSTMENT and WASTE are accepted. */
  type: Extract<MovementType, 'MANUAL_ADJUSTMENT' | 'WASTE'>;
  /** Signed integer. Positive = stock in, negative = stock out. Non-zero. */
  quantity: number;
  unitCost?: number;
  notes?: string;
  /** Phase A: named lot for lot-tracked items. Mutually exclusive with forceNullLotOverride. */
  lotId?: string;
  /** Phase A: post to null-lot bucket on a lot-tracked item. Mutually exclusive with lotId. */
  forceNullLotOverride?: boolean;
  /** Phase A: allow resulting lot balance to go negative. */
  forceNegativeOverride?: boolean;
  /** INVENTORY-TRUST.1d: client UUID per submission; retries reuse it (idempotent). */
  idempotencyKey?: string;
}

/** Backend returns both the created movement and the updated balance in one response. */
export interface ManualMovementResult {
  movement: InventoryMovement;
  balance: InventoryBalance;
}

// ── Opening stock (INVENTORY-TRUST.1a) ────────────────────────────────────────

export interface OpeningStockPayload {
  /** The VenueLocation for the starting balance. Required. */
  locationId: string;
  /** Positive number — the starting on-hand quantity. */
  quantity: number;
  /** Optional cost per unit; recorded on the movement and as the balance lastCost. */
  unitCost?: number;
}

export interface OpeningStockLocationEligibility {
  locationId: string;
  name: string;
  isDefault: boolean;
  active: boolean;
  /** true when the item has no movements at this location (opening allowed). */
  eligible: boolean;
  hasMovements: boolean;
  hasOpening: boolean;
}

export interface OpeningStockEligibility {
  itemId: string;
  locations: OpeningStockLocationEligibility[];
}

const inventoryMovementService = {
  /**
   * Get movements for a venue, optionally filtered by inventoryItemId, wineId, or type.
   * Always returned newest-first (postedAt DESC).
   */
  getMovements: async (
    venueId: string,
    page = 1,
    pageSize = 50,
    inventoryItemId?: string,
    wineId?: string,
    type?: MovementType,
  ): Promise<PaginatedResponse<InventoryMovement>> => {
    const params: Record<string, unknown> = { page, pageSize };
    if (inventoryItemId) params.inventoryItemId = inventoryItemId;
    if (wineId) params.wineId = wineId;
    if (type) params.type = type;
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  /**
   * Post a manual adjustment or waste movement.
   * Returns both the created movement and the updated balance — no second round-trip needed.
   * RECEIVE, COUNT_VARIANCE, and TRANSFER movements are created by their own workflows.
   */
  postManualMovement: async (
    venueId: string,
    payload: ManualMovementPayload,
  ): Promise<ManualMovementResult> => {
    const response = await api.post(base(venueId), payload);
    return response.data;
  },

  /**
   * Set the opening (genesis) stock for an item at a location with no prior
   * activity. Books to venue-owned stock; one opening per item+location.
   */
  postOpeningStock: async (
    venueId: string,
    inventoryItemId: string,
    payload: OpeningStockPayload,
  ): Promise<ManualMovementResult> => {
    const response = await api.post(
      `/admin/venue/${venueId}/inventory/item/${inventoryItemId}/opening-stock`,
      payload,
    );
    return response.data;
  },

  /** Per-location opening-stock eligibility for the dialog's location picker. */
  getOpeningStockEligibility: async (
    venueId: string,
    inventoryItemId: string,
  ): Promise<OpeningStockEligibility> => {
    const response = await api.get(
      `/admin/venue/${venueId}/inventory/item/${inventoryItemId}/opening-stock/eligibility`,
    );
    return response.data;
  },
};

export default inventoryMovementService;
